import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import flatten from 'flat-util';

import { LawMenuList } from 'src/components/JobChecklistForm/LawMenuList';
import { LawOption } from 'src/components/JobChecklistForm/LawOption';
import { LawSingleValue } from 'src/components/JobChecklistForm/LawSingleValue';
import { useChecklistQuestionVersionLaws } from 'src/hooks/checklistQuestion';
import s from 'src/components/JobChecklistForm/JobChecklistForm.styles';
import SelectBox from 'src/components/SelectBox';
import TextField from 'src/components/TextField';
import Checkbox from 'src/components/Checkbox';

export const NonComplianceBlock = ({
  activeQuestion,
  factory,
  index,
  isBlockSaveFailed,
  isBlockSaveSuccess,
  isFiltered,
  countryId,
  nonCompliance,
  nonCompliancesOptions,
  removeResultNonCompliance,
  setIsFiltered,
  setResultNonCompliance,
  setResultNonComplianceByObject,
  isNegativeOutcomeSelected,
  checklist,
}) => {
  const { t } = useTranslation();
  const [localNonComplianceValue, setLocalNonComplianceValue] = useState(null);

  useEffect(() => {
    setLocalNonComplianceValue(nonCompliance);
  }, [nonCompliance]);

  const { data: checklistQuestionWithLaws, loading: loadingLaws } = useChecklistQuestionVersionLaws(
    activeQuestion.checklistQuestion.currentVersion.id,
    countryId);

  const nonCompliances = checklistQuestionWithLaws?.checklistQuestionVersion?.nonCompliances
    ?.filter(v => v.id === nonCompliance.nonComplianceId || v.id === nonCompliance.id);
  const nonComplianceLaws = flatten(nonCompliances?.map(v => v.laws), true).filter(v => !!v.currentVersion);

  let countryLaws = [];
  if (factory) {
    const companyAddress = factory.company.currentVersion.companyAddresses[0];

    if (companyAddress && companyAddress.currentVersion.country) {
      countryLaws = companyAddress.currentVersion.country.laws?.filter(law => !!law.currentVersion);
    }
  }

  const customLaw = nonCompliance.law ? [{
    id: nonCompliance.law,
    currentVersion: {
      legislationName: nonCompliance.law,
      articleSecNo: null,
      legalCitation: null,
      legalReqSummary: null,
      internalGuidelineInterpretation: null,
    },
    isCustom: true,
  }]
    : [];

  const allLawsOptions = [
    ...nonComplianceLaws,
    ...countryLaws,
    ...customLaw,
  ].map(v => ({
    articleSecNo: v.currentVersion.articleSecNo,
    internalGuidelineInterpretation: v.currentVersion.internalGuidelineInterpretation,
    label: v.currentVersion.legislationName,
    legalCitation: v.currentVersion.legalCitation,
    legalReqSummary: v.currentVersion.legalReqSummary,
    value: v.id,
  }));

  const lawsOptions = (isFiltered ? nonComplianceLaws : countryLaws)
    .map(v => ({
      articleSecNo: v.currentVersion.articleSecNo,
      internalGuidelineInterpretation: v.currentVersion.internalGuidelineInterpretation,
      label: v.currentVersion.legislationName,
      legalCitation: v.currentVersion.legalCitation,
      legalReqSummary: v.currentVersion.legalReqSummary,
      value: v.id,
    }));

  const previousOrNewFindingOptions = [
    {
      label: t('FORM_PREVIOUS_OR_NEW_VALUE_NEW'),
      value: 'new',
    },
    {
      label: t('FORM_PREVIOUS_OR_NEW_VALUE_PREVIOUS'),
      value: 'previous',
    },
  ];

  return (
    <s.NonComplianceWrapper
      error={isBlockSaveFailed(`nonCompliances${index}`)}
      flash={isBlockSaveSuccess(`nonCompliances${index}`)}
      >
      <span>{index + 1}.</span>
      <Checkbox
        disabled
        label={t('FORM_HIDDEN_FINDING')}
        value={nonCompliance.isHiddenInDraftCap || false}
        reverse
      />
      <SelectBox
        disabled={!isNegativeOutcomeSelected}
        label={t('FORM_PREVIOUS_OR_NEW')}
        onChange={value => setResultNonCompliance(
          activeQuestion,
          index,
          'findingType',
          value ? value.value : null,
        )}
        options={previousOrNewFindingOptions}
        value={previousOrNewFindingOptions.find(v => v.value === nonCompliance.findingType)}
      />
      <SelectBox
        label={t('FORM_NON_COMPLIANCE')}
        onChange={value => setResultNonCompliance(
          activeQuestion,
          index,
          'nonComplianceId',
          value ? value.value : null,
        )}
        options={nonCompliancesOptions}
        value={nonCompliance.nonComplianceId ? nonCompliancesOptions.find(v => v.value === nonCompliance.nonComplianceId) : null}
      />
      {checklist?.currentVersion?.hasLegalRequirements && (
        <>
          <SelectBox
            components={{
              SingleValue: LawSingleValue,
              Option: LawOption,
              // eslint-disable-next-line react/display-name
              MenuList: props => (
                <LawMenuList
                  {...props}
                  isFiltered={isFiltered}
                  setIsFiltered={setIsFiltered}
                />
              ),
            }}
            filterOption={({ data }, str) => {
              // eslint-disable-next-line no-underscore-dangle
              if (!str || data.__isNew__) {
                return true;
              }

              const q = str.toLowerCase();
              return (data.articleSecNo && data.articleSecNo.toLowerCase().indexOf(q) !== -1) ||
                (data.label && data.label.toLowerCase().indexOf(q) !== -1) ||
                (data.legalCitation && data.legalCitation.toLowerCase().indexOf(q) !== -1) ||
                (data.legalReqSummary && data.legalReqSummary.toLowerCase().indexOf(q) !== -1) ||
                (data.internalGuidelineInterpretation &&
                  data.internalGuidelineInterpretation.toLowerCase().indexOf(q) !== -1);
            }}
            label={t('FORM_LAW')}
            onChange={value => setResultNonComplianceByObject(
              activeQuestion,
              index,
              {
                lawId: value ? value.value : null,
                law: null,
              },
            )}
            isLoading={loadingLaws}
            options={lawsOptions}
            value={(nonCompliance.lawId || nonCompliance.law) ? allLawsOptions.find(v => v.value === (nonCompliance.lawId || nonCompliance.law)) : null}
          />
          <TextField
            label={t('FORM_LEGAL_REQUIREMENTS')}
            multiline
            onBlur={e => {
              if (localNonComplianceValue.legalRequirements !== nonCompliance.legalRequirements) {
                setResultNonCompliance(
                  activeQuestion,
                  index,
                  'legalRequirements',
                  localNonComplianceValue.legalRequirements,
                )
              }
            }}
            onChange={e => setLocalNonComplianceValue({
              ...localNonComplianceValue,
              legalRequirements: e.target.value,
            })}
            id={`legal-requirements${index}`}
            value={localNonComplianceValue?.legalRequirements || ''}
          />
        </>
      )}
      <TextField
        label={t('FORM_DETAILED_FINDING')}
        multiline
        onBlur={e => {
          if (localNonComplianceValue.detailedFinding !== nonCompliance.detailedFinding) {
            setResultNonCompliance(
              activeQuestion,
              index,
              'detailedFinding',
              localNonComplianceValue.detailedFinding,
            )
          }
        }}
        onChange={e => setLocalNonComplianceValue({
          ...localNonComplianceValue,
          detailedFinding: e.target.value,
        })}
        id={`detailed-finding${index}`}
        value={localNonComplianceValue?.detailedFinding || ''}
      />
      {checklist?.currentVersion?.hasRecommendations && (
        <TextField
          label={t('FORM_RECOMMENDATIONS')}
          multiline
          onBlur={e => {
            if (localNonComplianceValue.recommendations !== nonCompliance.recommendations) {
              setResultNonCompliance(
                activeQuestion,
                index,
                'recommendations',
                localNonComplianceValue.recommendations,
              )
            }
          }}
          onChange={e => setLocalNonComplianceValue({
            ...localNonComplianceValue,
            recommendations: e.target.value,
          })}
          id={`recommendations${index}`}
          value={localNonComplianceValue?.recommendations || ''}
        />
      )}
      <s.RemoveButton
        name="remove"
        onClick={() => removeResultNonCompliance(
          activeQuestion,
          index,
        )}
      />
    </s.NonComplianceWrapper>
  );
};
